<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAttendanceStore, useOrganizationStore } from '@/store';
import { StudentAttendanceInterface } from '@/types';
import AutoComplete from 'primevue/autocomplete';
import Divider from 'primevue/divider';
import InputNumber from 'primevue/inputnumber';
import OverlayPanel from 'primevue/overlaypanel';
import AttendanceChart from './AttendanceChart.vue';
import StudentPercentage from './StudentPercentage.vue';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

const AttendanceStore = useAttendanceStore();
const organizationStore = useOrganizationStore();
const { lowAttendance, outOfLowAttendance, lowAttendanceThreshold, allPercentage, loading, filteredStudent } =
  storeToRefs(AttendanceStore);

const oldLowAttendanceThreshold = ref(lowAttendanceThreshold.value);
const students = computed(() => [...lowAttendance.value, ...outOfLowAttendance.value]);
const filteredStudents = ref(students.value);
const { organizationsIdsSelected } = storeToRefs(organizationStore);
const studentSelectedKey = ref('');

onMounted(() => {
  updateStudentsLowAttendance();
});

const updateStudentsLowAttendance = () => {
  filteredStudents.value = students.value;
};

const updateThreshold = async () => {
  if (oldLowAttendanceThreshold.value === lowAttendanceThreshold.value) return;

  await AttendanceStore.getLowAttendance(lowAttendanceThreshold.value, organizationsIdsSelected.value);
  updateStudentsLowAttendance();
};

const onStudentSelected = (student: StudentAttendanceInterface, key: string) => {
  if (key === studentSelectedKey.value || key === '') {
    studentSelectedKey.value = '';
    filteredStudent.value = null;
    return;
  }
  filteredStudent.value = student;
  studentSelectedKey.value = key;
};

const searchStudent = (event) => {
  if (event.query.trim().length < 1) {
    return;
  }

  filteredStudents.value = students.value.filter((student: StudentAttendanceInterface) => {
    return student.full_name.toLowerCase().search(event.query.toLowerCase()) >= 0;
  });
};

watch(
  () => organizationsIdsSelected.value,
  () => {
    updateThreshold();
  },
);

watch(filteredStudent, () => {
  if (filteredStudent.value?.id) {
    AttendanceStore.getAttendanceResults(
      filteredStudent.value?.id,
      filteredStudent.value?.full_name,
      organizationsIdsSelected.value,
    );
    studentSelectedKey.value = `k${filteredStudent.value.id}`;
    return;
  }
  AttendanceStore.useCloneResults();
  studentSelectedKey.value = '';
});

const thresholdOverlay = ref();
const openThresholdOverlay = (Event: Event) => {
  thresholdOverlay.value.toggle(Event);
};
</script>

<template>
  <div class="flex flex-1">
    <div class="w-6 mr-3">
      <div class="w-full p-input-icon-left mb-3">
        <IconField icon-position="left">
          <InputIcon class="p-input-icon-left" style="z-index: 100">
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <AutoComplete
            v-model="filteredStudent"
            class="autocomplete w-full"
            input-class="w-full"
            :suggestions="filteredStudents"
            placeholder="Search Students"
            :min-length="2"
            field="full_name"
            data-key="id"
            @complete="searchStudent($event)"
          />
        </IconField>
      </div>

      <div
        v-if="can([PERMISSIONS.DASHBOARD_OVERVIEW_ATTENDACE_WIDGET_PERCENTAGE_VIEW])"
        class="flex align-items-center justify-content-between p-1 border-round-lg surface-100 cursor-pointer"
        :class="{ 'bg-primary-100': !filteredStudent?.id, raised: filteredStudent?.id }"
      >
        <p
          class="w-full flex align-items-center justify-content-between p-1 font-bold border-round-md"
          style="height: 28px"
          :class="{ 'bg-primary-100': !filteredStudent?.id }"
          @click="() => onStudentSelected(null, '')"
        >
          <span>All</span>
          <span>{{ allPercentage }}%</span>
        </p>
      </div>

      <Divider />
      <div
        v-if="can([PERMISSIONS.DASHBOARD_OVERVIEW_ATTENDACE_WIDGET_PERCENTAGE_VIEW])"
        class="overflow-y-scroll scrollbar max-height"
      >
        <div class="flex align-items-center mb-2">
          <p class="alert-red mr-2">Low Attendance (&lt;{{ lowAttendanceThreshold }}%)</p>
          <Icon name="edit" class="text-black-alpha-20 cursor-pointer text-sm" @click="openThresholdOverlay" />
          <OverlayPanel ref="thresholdOverlay" class="p-3" @hide="updateThreshold()">
            <p class="mb-2">Threshold for "Low Attendance" Warning</p>
            <InputNumber
              v-model="lowAttendanceThreshold"
              show-buttons
              button-layout="horizontal"
              suffix=" %"
              :min="0"
              :max="100"
              :step="1"
              increment-button-icon="pi pi-plus"
              decrement-button-icon="pi pi-minus"
            />
          </OverlayPanel>
        </div>

        <div v-if="lowAttendance.length > 0">
          <StudentPercentage
            v-for="student in lowAttendance"
            :key="`k${student.id}`"
            :skey="`k${student.id}`"
            :student="student"
            :selected="studentSelectedKey === `k${student.id}`"
            :low-attendance="true"
            @student-selected="onStudentSelected"
          />
        </div>
        <p v-else class="p-2 surface-100 mb-3 border-round-sm">No students with low attendance</p>

        <p class="text-md text-gray-600 mb-2">Students with good attendance</p>
        <div v-if="outOfLowAttendance.length > 0">
          <StudentPercentage
            v-for="student in outOfLowAttendance"
            :key="`k${student.id}`"
            :skey="`k${student.id}`"
            :student="student"
            :selected="studentSelectedKey === `k${student.id}`"
            :low-attendance="false"
            @student-selected="onStudentSelected"
          />
        </div>
        <p v-else class="p-2 surface-100 mb-3 border-round-sm">No students with good attendance</p>
      </div>
    </div>
    <div
      v-if="can([PERMISSIONS.DASHBOARD_OVERVIEW_ATTENDACE_WIDGET_DETAIL_VIEW])"
      class="w-6 p-3 bg-primary-100 border-round-lg"
    >
      <AttendanceChart />

      <RouterLink
        v-if="!loading && filteredStudent?.id && can([PERMISSIONS.PERSON_LOGS_INDEX])"
        :to="`/user/${filteredStudent.id}/activity`"
        class="flex align-items-center font-bold no-underline mt-6"
      >
        <p class="text-md mr-2 cursor-pointer" style="color: #078888">See Details</p>
        <Icon name="arrow_forward" style="color: #8bcfcf" />
      </RouterLink>
    </div>
  </div>
</template>

<style scoped lang="scss">
.alert-red {
  color: $errorColor;
}
.max-height {
  max-height: 350px;
}

:deep(.p-autocomplete) {
  .p-autocomplete-input {
    padding-left: 35px;
  }
}

:deep(.p-button) {
  color: gray;
  font-weight: bold;
  background: #fff;
  border: 2px solid #f1f3f3;

  &:hover {
    color: gray;
    font-weight: bold;
    background: #fff;
    border: 2px solid #f1f3f3;
  }
}
</style>
